import { useEffect, useMemo, useState } from 'react';
import { BeforeInstallPromptEvent } from '../../pwa';
import type { AppNotification } from '../../features/notifications';
import { frontendConfig } from '../../shared/config/env';

interface UseAppShellStateParams {
  bootstrap: () => Promise<void>;
  notifications: AppNotification[];
  dismissNotification: (id: string) => void;
  notify: (message: string, level?: AppNotification['type']) => void;
}

export function useAppShellState({
  bootstrap,
  notifications,
  dismissNotification,
  notify,
}: UseAppShellStateParams) {
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  const isStandalone = useMemo(() => {
    const iosNavigator = window.navigator as Navigator & {
      standalone?: boolean;
    };
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      iosNavigator.standalone === true
    );
  }, []);

  const updatedAt = useMemo(() => {
    const updatedSource = new Date(frontendConfig.buildUpdatedAt);
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(updatedSource);
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      setInstallPrompt(installEvent);
    };

    const handleInstalled = () => {
      setInstallPrompt(null);
      notify('Приложение установлено.', 'success');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, [notify]);

  useEffect(() => {
    if (!notifications.length) {
      return;
    }

    const timers = notifications.map((notification) =>
      window.setTimeout(() => dismissNotification(notification.id), 4000)
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [notifications, dismissNotification]);

  const handleInstall = async () => {
    if (!installPrompt) {
      notify('Установка пока недоступна в этом браузере или уже выполнена.');
      return;
    }

    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      notify('Установка приложения подтверждена.', 'success');
      setInstallPrompt(null);
    }
  };

  return {
    handleInstall,
    installDisabled: !installPrompt || isStandalone,
    updatedAt,
  };
}
